import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ClipboardList, KeyRound, RefreshCw } from "lucide-react";
import { LOGO } from "../../data/content";
import { SalaryRequestSection } from "./SalaryRequestSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70";
const label = "block text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2";
const btn = "w-full bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3.5 transition-colors disabled:opacity-50";

const badge = (s) =>
  s === "approved" || s === "overtime_approved" || s === "comp_off_granted"
    ? "text-emerald-300 border-emerald-500/50"
    : s === "rejected"
      ? "text-red-300 border-red-500/50"
      : "text-amber-300 border-amber-500/50";

const Badge = ({ s }) => (
  <span className={`text-[10px] tracking-[0.14em] uppercase border px-2 py-0.5 ${badge(s)}`}>{(s || "pending").replaceAll("_", " ")}</span>
);

const Section = ({ title, items, empty, render, testId }) => (
  <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={testId}>
    <h3 className="text-white text-sm mb-4">{title} <span className="text-slate-500">({items.length})</span></h3>
    {items.length === 0 ? <p className="text-xs text-slate-500">{empty}</p> : <div className="grid gap-2">{items.map(render)}</div>}
  </div>
);

const fmtDateTime = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function MyRequests() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email");
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/my-requests/otp/send`, { email });
      toast.success("Verification code sent to your email");
      setStep("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/my-requests/otp/verify`, { email, code });
      setToken(res.token);
      await load(res.token);
      setStep("list");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const load = async (tok = token) => {
    try {
      const { data: res } = await axios.get(`${API}/my-requests`, { params: { token: tok } });
      setData(res);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — verify again");
        setStep("email");
        setToken(null);
        setData(null);
      } else {
        toast.error("Could not load your requests");
      }
    }
  };

  return (
    <div className="min-h-screen bg-nx-navy px-6 py-16" data-testid="my-requests-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-12 w-auto mx-auto mb-6" />
          <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">Employee Self-Service</p>
          <h1 className="font-marcellus text-3xl text-white mt-3">My Requests</h1>
          <p className="text-sm text-slate-500 mt-3">Check the status of your leave applications, attendance corrections and field duty entries.</p>
        </div>

        {step === "email" && (
          <form onSubmit={sendOtp} className="border border-white/10 bg-white/[0.02] p-7 space-y-5 max-w-md mx-auto">
            <label className="block">
              <span className={label}>Your email registered with HR</span>
              <input type="email" required className={input} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="myreq-email" />
            </label>
            <button type="submit" disabled={busy} className={btn} data-testid="myreq-send-otp"><KeyRound size={13} className="inline mr-1" /> {busy ? "Sending…" : "Send Verification Code"}</button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verify} className="border border-white/10 bg-white/[0.02] p-7 space-y-5 max-w-md mx-auto">
            <p className="text-xs text-slate-400">A 6-digit code was sent to <span className="text-white">{email}</span>. It expires in 10 minutes.</p>
            <label className="block">
              <span className={label}>Verification code</span>
              <input required inputMode="numeric" maxLength={6} className={`${input} text-center text-xl tracking-[0.5em]`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" data-testid="myreq-otp" />
            </label>
            <button type="submit" disabled={busy} className={btn} data-testid="myreq-verify">{busy ? "Verifying…" : "Verify & View"}</button>
            <button type="button" onClick={() => setStep("email")} className="w-full text-xs text-slate-500 hover:text-white transition-colors" data-testid="myreq-back">Use a different email</button>
          </form>
        )}

        {step === "list" && data && (
          <div className="space-y-6" data-testid="myreq-list">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-400">Requests for <span className="text-white">{data.employee_name || email}</span></p>
              <button onClick={() => load()} className="border border-white/15 hover:border-nx-gold/60 text-slate-300 hover:text-white text-xs tracking-[0.12em] uppercase px-4 py-2 transition-colors" data-testid="myreq-refresh"><RefreshCw size={12} className="inline mr-1" /> Refresh</button>
            </div>
            {data.leave_balances?.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="myreq-balances">
                <h3 className="text-white text-sm mb-4">Leave Balance</h3>
                <div className="grid gap-2">
                  {data.leave_balances.map((b) => (
                    <div key={b.leave_type} className="flex items-center justify-between text-xs border-b border-white/5 pb-2" data-testid={`myreq-balance-${b.leave_type.replaceAll(" ", "-").toLowerCase()}`}>
                      <span className="text-slate-200">{b.leave_type}</span>
                      <span className="text-slate-400">used {b.used} of {b.allocated} · <span className={b.remaining > 0 ? "text-emerald-300" : "text-red-300"}>{b.remaining} left</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <SalaryRequestSection token={token} />
            <Section title="Leave Requests" items={data.leave} empty="No leave requests yet." testId="myreq-leave"
              render={(r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-b border-white/5 pb-2" data-testid={`myreq-leave-${r.id}`}>
                  <span className="text-slate-200">{r.leave_type} · {r.from_date} → {r.to_date}</span>
                  <Badge s={r.status} />
                  <span className="text-slate-600">{fmtDateTime(r.created_at)}</span>
                  {r.status === "rejected" && r.rejection_comment && <span className="text-red-300/70 w-full">Reason: {r.rejection_comment}</span>}
                </div>
              )} />
            <Section title="Attendance Corrections" items={data.corrections} empty="No correction requests yet." testId="myreq-corrections"
              render={(r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-b border-white/5 pb-2" data-testid={`myreq-corr-${r.id}`}>
                  <span className="text-slate-200">{r.date} · missed {r.punch_type === "IN" ? "check-in" : "check-out"} → {r.requested_time?.slice(-8, -3)}</span>
                  <Badge s={r.status} />
                  {r.status === "rejected" && r.rejection_comment && <span className="text-red-300/70 w-full">Reason: {r.rejection_comment}</span>}
                </div>
              )} />
            <Section title="Field Duty" items={data.field_duty} empty="No field duty entries yet." testId="myreq-fieldduty"
              render={(r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-b border-white/5 pb-2" data-testid={`myreq-fd-${r.id}`}>
                  <span className="text-slate-200">{r.date} · {r.customer} · {r.check_in} → {r.check_out || "on site"}</span>
                  {r.ot_minutes > 0 && !r.ot_decision && <Badge s="pending" />}
                  {r.ot_decision && <Badge s={r.ot_decision} />}
                  {r.ot_minutes > 0 && <span className="text-slate-600">OT {r.ot_minutes}m</span>}
                </div>
              )} />
            <p className="text-[11px] text-slate-600 text-center"><CheckCircle2 size={11} className="inline mr-1" /> Decisions are also emailed to you. Session expires in 30 minutes.</p>
          </div>
        )}
      </div>
    </div>
  );
}
